import React, { useState } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Input from "components/atoms/Input";
import InfoIconButton from "components/atoms/InfoIconButton";

interface AddPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (patientData: {
    birthYear: string;
    gender: string;
    memo: string;
  }) => void;
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const FormField = styled.div`
  margin-bottom: 20px;
`;

const TwoColumnContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
  align-items: flex-start;
`;

const ColumnWrapper = styled.div`
  flex: 1;
  min-width: 0; /* flex child가 내용보다 작아질 수 있도록 */
  display: flex;
  flex-direction: column;
`;

const LabelWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

const GenderButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const AddPatientModal: React.FC<AddPatientModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [birthYear, setBirthYear] = useState<string>("");
  const [age, setAge] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [memo, setMemo] = useState<string>("");

  // 출생년도로 나이 계산하는 함수
  const calculateAgeFromBirthYear = (birthYear: string): string => {
    if (!birthYear || isNaN(Number(birthYear))) return "";
    const currentYear = new Date().getFullYear();
    const calculatedAge = currentYear - Number(birthYear);
    if (calculatedAge < 0 || calculatedAge > 150) return ""; // 유효하지 않은 나이 범위
    return String(calculatedAge);
  };

  // 나이로 출생년도 계산하는 함수
  const calculateBirthYearFromAge = (age: string): string => {
    if (!age || isNaN(Number(age))) return "";
    const currentYear = new Date().getFullYear();
    const calculatedBirthYear = currentYear - Number(age);
    if (calculatedBirthYear < 1900 || calculatedBirthYear > currentYear) return ""; // 유효하지 않은 출생년도 범위
    return String(calculatedBirthYear);
  };

  // 출생년도 변경 핸들러
  const handleBirthYearChange = (value: string) => {
    setBirthYear(value);
    if (value) {
      const calculatedAge = calculateAgeFromBirthYear(value);
      setAge(calculatedAge);
    } else {
      setAge("");
    }
  };

  // 나이 변경 핸들러
  const handleAgeChange = (value: string) => {
    setAge(value);
    if (value) {
      const calculatedBirthYear = calculateBirthYearFromAge(value);
      setBirthYear(calculatedBirthYear);
    } else {
      setBirthYear("");
    }
  };

  const handleSubmit = () => {
    // 유효성 검사
    if (!birthYear && !age) {
      alert("출생년도 또는 나이를 입력해주세요.");
      return;
    }
    
    let finalBirthYear = birthYear;
    
    // 나이만 입력된 경우 출생년도 계산
    if (!birthYear && age) {
      finalBirthYear = calculateBirthYearFromAge(age);
    }
    
    const year = parseInt(finalBirthYear);
    if (isNaN(year) || year < 1900 || year > new Date().getFullYear()) {
      alert("올바른 출생년도 또는 나이를 입력해주세요.");
      return;
    }

    if (!gender) {
      alert("성별을 선택해주세요.");
      return;
    }

    onSubmit({
      birthYear: finalBirthYear,
      gender,
      memo,
    });

    // 폼 초기화
    setBirthYear("");
    setAge("");
    setGender("");
    setMemo("");
  };

  const handleClose = () => {
    // 폼 초기화
    setBirthYear("");
    setAge("");
    setGender("");
    setMemo("");
    onClose();
  };

  // [2025-06-20] 결정사항: 사용자 경험 개선을 위해 필수 입력값(나이/출생년도, 성별)이 모두 입력되었을 때만 버튼 활성화
  // 이는 사용자가 필수 정보를 놓치지 않도록 도움을 주고, 불필요한 에러 메시지를 줄입니다.
  const isFormValid = () => {
    const hasAgeOrBirthYear = birthYear.trim() !== "" || age.trim() !== "";
    const hasGender = gender !== "";
    return hasAgeOrBirthYear && hasGender;
  };

  // ESC 키로 모달 닫기
  React.useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);

  // 모달 외부 클릭으로 닫기
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <Text textType="H3" color={COLOR_SYSTEM.get("Gray")[700]}>
            새 환자 추가
          </Text>
          <Button
            size="Small"
            type="Icon"
            variant="Ghost"
            color="Secondary"
            icon="X"
            onClick={handleClose}
          />
        </ModalHeader>

        <FormField>
          <LabelWithTooltip>
            <Text
              textType="Body2"
              color={COLOR_SYSTEM.get("Gray")[600]}
            >
              출생년도 또는 나이 <span style={{ color: COLOR_SYSTEM.get("Red")[500] }}>*</span>
            </Text>
            <InfoIconButton
              tooltip="출생년도와 나이 둘 중 하나를 입력하면 자동으로 계산됩니다."
              iconSize={14}
              tooltipPosition="bottom"
              tooltipMaxWidth="280px"
            />
          </LabelWithTooltip>
          <TwoColumnContainer>
            <ColumnWrapper>
              <Input
                type="number"
                value={birthYear}
                onChange={(e) => handleBirthYearChange(e.target.value)}
                placeholder="출생년도 (예: 1950)"
                unit="년"
                style={{ width: '100%' }}
              />
            </ColumnWrapper>
            <ColumnWrapper>
              <Input
                type="number"
                value={age}
                onChange={(e) => handleAgeChange(e.target.value)}
                placeholder="나이 (예: 75)"
                unit="세"
                style={{ width: '100%' }}
              />
            </ColumnWrapper>
          </TwoColumnContainer>
        </FormField>

        <FormField>
          <Text
            textType="Body2"
            color={COLOR_SYSTEM.get("Gray")[600]}
            style={{ marginBottom: 8 }}
          >
            성별 <span style={{ color: COLOR_SYSTEM.get("Red")[500] }}>*</span>
          </Text>
          <GenderButtonContainer>
            <Button
              size="Medium"
              type="Text"
              variant={gender === '남' ? "Contained" : "Outlined"}
              color={gender === '남' ? "Primary" : "Secondary"}
              onClick={() => setGender('남')}
              style={{ flex: 1 }}
            >
              남
            </Button>
            <Button
              size="Medium"
              type="Text"
              variant={gender === '여' ? "Contained" : "Outlined"}
              color={gender === '여' ? "Primary" : "Secondary"}
              onClick={() => setGender('여')}
              style={{ flex: 1 }}
            >
              여
            </Button>
          </GenderButtonContainer>
        </FormField>

        <FormField>
          <LabelWithTooltip>
            <Text
              textType="Body2"
              color={COLOR_SYSTEM.get("Gray")[600]}
            >
              환자 식별용 메모
            </Text>
            <InfoIconButton
              tooltip="AI에게는 제공되지 않는 정보입니다. 약사님이 환자를 기억하기 위한 정보를 입력하세요. ※ 이름, 주소, 주민등록번호, 전화번호 등 개인정보 입력 금지"
              iconSize={14}
              tooltipPosition="bottom"
              tooltipMaxWidth="300px"
            />
          </LabelWithTooltip>
          <Input
            type="textarea"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="예: 다제0712방문예정, 방문약료2"
            style={{ minHeight: "100px", resize: "vertical" }}
          />
        </FormField>

        <ButtonContainer>
          <Button
            size="Medium"
            type="Text"
            variant="Outlined"
            color="Secondary"
            onClick={handleClose}
          >
            취소
          </Button>
          <Button
            size="Medium"
            type="Text"
            variant="Contained"
            color={isFormValid() ? "Primary" : "Tertiary"}
            onClick={handleSubmit}
            disabled={!isFormValid()}
          >
            환자 추가
          </Button>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddPatientModal; 